import type { BenefitFieldDef, BenefitInputs, BenefitRule, RuleSetRecord } from '../types/rulesEngine'

export const BENEFIT_FIELDS: BenefitFieldDef[] = [
  { key: 'networkStatus', label: 'Network Status', type: 'select', options: ['in-network', 'out-of-network', 'out-of-area'] },
  { key: 'coverageActive', label: 'Active Coverage', type: 'select', options: ['yes', 'no'] },
  { key: 'benefitCovered', label: 'Benefit Covered', type: 'select', options: ['yes', 'no'] },
  { key: 'deductibleTotal', label: 'Deductible Total ($)', type: 'number' },
  { key: 'deductibleAccumulated', label: 'Deductible Accumulated ($)', type: 'number' },
  { key: 'deductibleMet', label: 'Deductible Met (%)', type: 'number' },
  { key: 'oopMax', label: 'OOP Maximum ($)', type: 'number' },
  { key: 'oopAccumulated', label: 'OOP Accumulated ($)', type: 'number' },
  { key: 'coinsurancePct', label: 'Coinsurance (%)', type: 'number' },
  { key: 'coveragePct', label: 'Coverage (%)', type: 'number' },
  { key: 'copay', label: 'Copay ($)', type: 'number' },
  { key: 'priorAuthRequired', label: 'Prior Auth Required', type: 'select', options: ['yes', 'no'] },
  { key: 'unitsRemaining', label: 'Units/Visits Remaining', type: 'number' },
]

export const DEFAULT_BENEFIT_INPUTS: BenefitInputs = {
  networkStatus: 'in-network',
  coverageActive: 'yes',
  benefitCovered: 'yes',
  deductibleTotal: 1500,
  deductibleAccumulated: 1500,
  deductibleMet: 100,
  oopMax: 6000,
  oopAccumulated: 2300,
  coinsurancePct: 20,
  coveragePct: 80,
  copay: 35,
  priorAuthRequired: 'yes',
  unitsRemaining: 4,
}

export const RULE_OPERATORS = [
  { key: 'equals', label: 'equals' },
  { key: 'not_equals', label: '≠ not equals' },
  { key: 'gt', label: '> greater than' },
  { key: 'gte', label: '≥ greater or equal' },
  { key: 'lt', label: '< less than' },
  { key: 'lte', label: '≤ less or equal' },
] as const

export const RULE_ACTIONS = [
  { key: 'continue', label: 'Continue to next rule' },
  { key: 'approve', label: 'Approve' },
  { key: 'deny', label: 'Deny' },
  { key: 'route_prior_auth', label: 'Route to Prior Auth' },
  { key: 'flag', label: 'Flag for Review' },
] as const

const CPAP_RULES: BenefitRule[] = [
  { id: 'r1', name: 'Network Status Check', field: 'networkStatus', operator: 'equals', value: 'in-network', action: 'continue', failAction: 'flag', failMessage: 'Patient is out-of-network — verify OON benefits or request GAP exception' },
  { id: 'r2', name: 'Deductible Met', field: 'deductibleMet', operator: 'gte', value: '100', action: 'continue', failAction: 'flag', failMessage: 'Deductible not fully met — calculate patient responsibility' },
  { id: 'r3', name: 'OOP Max Check', field: 'oopAccumulated', operator: 'lt', value: 'oopMax', action: 'continue', failAction: 'approve', failMessage: 'OOP max reached — approved at 100% coverage' },
  { id: 'r4', name: 'Prior Auth Required', field: 'priorAuthRequired', operator: 'equals', value: 'yes', action: 'route_prior_auth', failAction: 'continue', failMessage: 'Prior authorization required before fulfillment' },
  { id: 'r5', name: 'Coverage Percentage', field: 'coveragePct', operator: 'gte', value: '50', action: 'approve', failAction: 'deny', failMessage: 'Coverage below minimum threshold (50%)' },
]

const DME_RULES: BenefitRule[] = [
  { id: 'r1', name: 'Network Status Check', field: 'networkStatus', operator: 'equals', value: 'in-network', action: 'continue', failAction: 'flag', failMessage: 'Out-of-network — route to OON benefits team' },
  { id: 'r2', name: 'Active Coverage', field: 'coverageActive', operator: 'equals', value: 'yes', action: 'continue', failAction: 'deny', failMessage: 'No active coverage found' },
  { id: 'r3', name: 'Prior Auth Required', field: 'priorAuthRequired', operator: 'equals', value: 'yes', action: 'route_prior_auth', failAction: 'continue', failMessage: 'Prior auth needed' },
  { id: 'r4', name: 'Coinsurance Check', field: 'coinsurancePct', operator: 'lte', value: '40', action: 'approve', failAction: 'flag', failMessage: 'High coinsurance — confirm patient financial responsibility' },
]

const ORTHOTICS_RULES: BenefitRule[] = [
  { id: 'r1', name: 'Network Status Check', field: 'networkStatus', operator: 'equals', value: 'in-network', action: 'continue', failAction: 'flag', failMessage: 'Out-of-network provider' },
  { id: 'r2', name: 'Benefit Category Covered', field: 'benefitCovered', operator: 'equals', value: 'yes', action: 'continue', failAction: 'deny', failMessage: 'Orthotics not covered under plan' },
  { id: 'r3', name: 'Deductible Met', field: 'deductibleMet', operator: 'gte', value: '100', action: 'continue', failAction: 'flag', failMessage: 'Deductible not met' },
  { id: 'r4', name: 'Prior Auth Required', field: 'priorAuthRequired', operator: 'equals', value: 'yes', action: 'route_prior_auth', failAction: 'continue', failMessage: 'Prior auth required for orthotics' },
  { id: 'r5', name: 'Visit/Unit Limit', field: 'unitsRemaining', operator: 'gte', value: '1', action: 'approve', failAction: 'deny', failMessage: 'Annual unit limit exhausted' },
]

function seedRuleSet(
  id: string,
  name: string,
  rules: BenefitRule[],
  productServiceSlug?: string
): RuleSetRecord {
  const now = new Date().toISOString()
  return {
    id,
    name,
    productServiceSlug,
    rules: JSON.parse(JSON.stringify(rules)),
    isActive: true,
    createdAt: now,
    updatedAt: now,
  }
}

export const DEFAULT_RULE_SETS: RuleSetRecord[] = [
  seedRuleSet('cpap', 'CPAP', CPAP_RULES, 'cpap'),
  seedRuleSet('dme-general', 'DME General', DME_RULES, 'dme'),
  seedRuleSet('orthotics', 'Orthotics', ORTHOTICS_RULES, 'orthotics'),
]

export function createEmptyRule(): BenefitRule {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: 'New Rule',
    field: 'networkStatus',
    operator: 'equals',
    value: 'in-network',
    action: 'continue',
    failAction: 'flag',
    failMessage: 'Rule failed',
  }
}
